/**
 * @fileoverview Date Formatting Utilities
 * @description Centralized date formatting functions with proper null/undefined handling
 * and edge case validation. All functions return user-friendly strings.
 */

/**
 * Validate if a date value is valid
 * @param {*} dateValue - Value to validate (can be string, Date, number, or null/undefined)
 * @returns {boolean} True if valid date, false otherwise
 */
export function isValidDate(dateValue) {
  if (!dateValue) {
    return false
  }

  const date = new Date(dateValue)
  return date instanceof Date && !isNaN(date.getTime())
}

/**
 * Format date to locale string with proper null handling
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {Object} [options] - Intl.DateTimeFormat options
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Formatted date or fallback
 * @example
 * formatDate('2024-01-23T10:30:00Z') // 'Jan 23, 2024'
 * formatDate(null) // 'N/A'
 * formatDate('invalid') // 'N/A'
 */
export function formatDate(dateValue, options = null, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const date = new Date(dateValue)
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }

  return date.toLocaleDateString('UTC', options || defaultOptions)
}

/**
 * Format date with time to locale string with proper null handling
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {Object} [options] - Intl.DateTimeFormat options
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Formatted date with time or fallback
 * @example
 * formatDateTime('2024-01-23T10:30:00Z') // 'Jan 23, 2024, 10:30 AM'
 * formatDateTime(null) // 'N/A'
 */
export function formatDateTime(dateValue, options = null, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const date = new Date(dateValue)
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }

  return date.toLocaleString('UTC', options || defaultOptions)
}

/**
 * Format date as relative time (e.g., "2h ago", "5 days ago")
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Relative time string or fallback
 * @example
 * formatRelativeTime('2024-01-23T10:30:00Z') // '2h ago'
 * formatRelativeTime(null) // 'N/A'
 */
export function formatRelativeTime(dateValue, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const date = new Date(dateValue)
  const now = new Date()
  const diffMs = now - date

  // Handle future dates
  if (diffMs < 0) {
    return 'Just now'
  }

  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30))
  const diffYears = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365))

  if (diffSeconds < 60) {
    return 'Just now'
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }

  if (diffHours < 24) {
    return `${diffHours}h ago`
  }

  if (diffDays < 30) {
    return `${diffDays}d ago`
  }

  if (diffMonths < 12) {
    return `${diffMonths}mo ago`
  }

  return `${diffYears}y ago`
}

/**
 * Format time remaining until a future date
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Time remaining string, 'Expired', or fallback
 * @example
 * formatTimeRemaining('2024-01-24T10:30:00Z') // '2h 15m'
 * formatTimeRemaining('2024-01-20T10:30:00Z') // 'Expired'
 * formatTimeRemaining(null) // 'N/A'
 */
export function formatTimeRemaining(dateValue, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const now = new Date()
  const targetDate = new Date(dateValue)
  const diffMs = targetDate - now

  // Already expired
  if (diffMs <= 0) {
    return 'Expired'
  }

  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // More than 1 day
  if (days > 0) {
    const remainingHours = hours % 24
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
  }

  // Less than 1 day
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }

  // Less than 1 hour
  return `${minutes}m`
}

/**
 * Format full ISO timestamp for display
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Full timestamp or fallback
 * @example
 * formatFullTimestamp('2024-01-23T10:30:00Z') // 'January 23, 2024 at 10:30:00 AM'
 * formatFullTimestamp(null) // 'N/A'
 */
export function formatFullTimestamp(dateValue, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const date = new Date(dateValue)
  return date.toLocaleString('UTC', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })
}

/**
 * Format timestamp for audit logs with relative and absolute time
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {string} [fallback='N/A'] - Fallback string for invalid dates
 * @returns {string} Formatted timestamp or fallback
 * @example
 * formatAuditTimestamp('2024-01-23T10:30:00Z') // '2h ago' or 'Jan 23, 2024, 10:30 AM'
 */
export function formatAuditTimestamp(dateValue, fallback = 'N/A') {
  if (!isValidDate(dateValue)) {
    return fallback
  }

  const date = new Date(dateValue)
  const now = new Date()
  const diffMs = now - date
  const diffMinutes = Math.floor(diffMs / (1000 * 60))

  // Show relative time for recent events (< 24h)
  if (diffMinutes < 1) {
    return 'Just now'
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }

  // Show full timestamp for older events
  return date.toLocaleString('UTC', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Get time difference in milliseconds
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {string|Date|null|undefined} [compareDate] - Date to compare against (defaults to now)
 * @returns {number|null} Milliseconds difference or null if invalid
 */
export function getTimeDifference(dateValue, compareDate = null) {
  if (!isValidDate(dateValue)) {
    return null
  }

  const date = new Date(dateValue)
  const compareAgainst = compareDate ? new Date(compareDate) : new Date()

  if (!isValidDate(compareAgainst)) {
    return null
  }

  return compareAgainst - date
}

/**
 * Check if date is expiring soon (within threshold hours)
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @param {number} [thresholdHours=2] - Hours threshold for "expiring soon"
 * @returns {boolean} True if expiring soon, false otherwise
 */
export function isExpiringSoon(dateValue, thresholdHours = 2) {
  if (!isValidDate(dateValue)) {
    return false
  }

  const now = new Date()
  const expiresAt = new Date(dateValue)
  const diffMs = expiresAt - now

  // Already expired or invalid
  if (diffMs <= 0) {
    return false
  }

  const diffHours = diffMs / (1000 * 60 * 60)
  return diffHours < thresholdHours
}

/**
 * Check if date is expired
 * @param {string|Date|null|undefined} dateValue - ISO timestamp, Date object, or null/undefined
 * @returns {boolean} True if expired, false otherwise (including if date is invalid)
 */
export function isExpired(dateValue) {
  if (!isValidDate(dateValue)) {
    return false
  }

  const now = new Date()
  const expiresAt = new Date(dateValue)
  return expiresAt <= now
}
